/**
 * Utilidades para la gestión de listas de reproducción (Playlists)
 */

export const MAX_PLAYLIST_NAME_LENGTH = 30;

// Expresión regular para caracteres permitidos: letras con acentos, números, espacios, guiones y guiones bajos
export const ALLOWED_PLAYLIST_NAME_REGEX = /^[a-zA-Z0-9áéíóúÁÉÍÓÚñÑüÜ _-]+$/;

/**
 * Sanitiza el nombre de una lista:
 * - Filtra caracteres especiales no permitidos
 * - Limita la longitud a 30 caracteres
 * - Limpia espacios extremos
 */
export const sanitizePlaylistName = (name) => {
  if (!name || typeof name !== 'string') return '';
  return name
    .replace(/[^a-zA-Z0-9áéíóúÁÉÍÓÚñÑüÜ _-]/g, '')
    .slice(0, MAX_PLAYLIST_NAME_LENGTH);
};

/**
 * Verifica si un carácter ingresado es válido para el nombre de una lista
 */
export const isValidPlaylistChar = (char) => {
  if (!char) return false;
  return /^[a-zA-Z0-9áéíóúÁÉÍÓÚñÑüÜ _-]$/.test(char);
};

/**
 * Convierte un string de tiempo a segundos numéricos.
 * Soporta: "45", "45s", "1:30", "01:30", "1m30s", "90"
 */
export const parseTimeStringToSeconds = (rawInput) => {
  if (typeof rawInput === 'number') {
    return isNaN(rawInput) || rawInput < 0 ? 0 : Math.floor(rawInput);
  }
  if (!rawInput || typeof rawInput !== 'string') return 0;
  const str = rawInput.trim().toLowerCase();
  if (!str) return 0;

  // Formato mm:ss o hh:mm:ss
  if (str.includes(':')) {
    const parts = str.split(':').map(p => Number(p) || 0);
    if (parts.length === 2) {
      return Math.max(0, parts[0] * 60 + parts[1]);
    }
    if (parts.length === 3) {
      return Math.max(0, parts[0] * 3600 + parts[1] * 60 + parts[2]);
    }
  }

  // Formato 1m30s o 45s
  const mMatch = str.match(/(\d+)\s*m/);
  const sMatch = str.match(/(\d+)\s*s/);
  if (mMatch || sMatch) {
    const m = mMatch ? Number(mMatch[1]) : 0;
    const s = sMatch ? Number(sMatch[1]) : 0;
    return Math.max(0, m * 60 + s);
  }

  // Número directo de segundos
  const num = Number(str.replace(/[^\d.]/g, ''));
  return isNaN(num) || num < 0 ? 0 : Math.floor(num);
};

/**
 * Convierte una duración en formato ISO 8601 (ej: "PT3M45S", "PT1H2M30S", "PT45S") a segundos numéricos
 */
export const parseISO8601Duration = (durationStr) => {
  if (!durationStr || typeof durationStr !== 'string') return 0;
  const match = durationStr.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/i);
  if (!match) return 0;
  const hours = parseInt(match[1] || 0, 10);
  const minutes = parseInt(match[2] || 0, 10);
  const seconds = parseInt(match[3] || 0, 10);
  return hours * 3600 + minutes * 60 + seconds;
};

/**
 * Formatea segundos a texto legible "mm:ss" o "h:mm:ss"
 */
export const formatSecondsToTime = (totalSec) => {
  const sec = Math.max(0, Number(totalSec) || 0);
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = Math.floor(sec % 60);
  if (h > 0) {
    return `${h}:${m < 10 ? '0' : ''}${m}:${s < 10 ? '0' : ''}${s}`;
  }
  return `${m}:${s < 10 ? '0' : ''}${s}`;
};

/**
 * Genera el nombre del archivo para exportar la lista según la especificación:
 * garaoke-[AAAA-MM-dd]_[nombre_de_lista].txt
 */
export const formatExportFilename = (playlistName) => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const dateStr = `${year}-${month}-${day}`;

  const cleanName = sanitizePlaylistName(playlistName)
    .trim()
    .replace(/\s+/g, '_') || 'Mi_lista';

  return `garaoke-${dateStr}_${cleanName}.txt`;
};

/**
 * Genera el contenido JSON para exportar la lista con metadata y nombre
 */
export const createExportPayload = (playlistName, items = []) => {
  return JSON.stringify({
    name: sanitizePlaylistName(playlistName).trim() || 'Mi lista',
    version: '2.0',
    exportDate: new Date().toISOString(),
    items: Array.isArray(items) ? items : []
  }, null, 2);
};

/**
 * Parsea el contenido de un archivo importado, compatible con formato nuevo y legado
 */
/**
 * Parsea el contenido de un archivo importado, compatible con formato nuevo, legado y tolerante a BOM o texto plano
 */
export const parseImportPayload = (fileText, rawFilename = '') => {
  if (!fileText || typeof fileText !== 'string' || !fileText.trim()) {
    throw new Error('El archivo seleccionado está vacío.');
  }

  // Limpiar UTF-8 BOM (\uFEFF) y espacios al inicio y final
  const cleanText = fileText.replace(/^\uFEFF/, '').trim();

  let parsed = null;

  // Intento 1: Parsear directamente como JSON
  try {
    parsed = JSON.parse(cleanText);
  } catch (jsonErr) {
    // Si falla el parseo directo, intentar buscar un array o bloque JSON dentro del texto
    const arrayMatch = cleanText.match(/\[[\s\S]*\]/);
    const objectMatch = cleanText.match(/\{[\s\S]*\}/);
    if (arrayMatch) {
      try {
        parsed = JSON.parse(arrayMatch[0]);
      } catch (e) {}
    } else if (objectMatch) {
      try {
        parsed = JSON.parse(objectMatch[0]);
      } catch (e) {}
    }
  }

  // Si se obtuvo un objeto JSON
  if (parsed) {
    // Formato estándar v2: { name, items, ... }
    if (typeof parsed === 'object' && !Array.isArray(parsed)) {
      const candidates = parsed.items || parsed.playlist || parsed.songs || parsed.videos || [];
      if (Array.isArray(candidates)) {
        return {
          name: sanitizePlaylistName(parsed.name || '').trim() || extractNameFromFilename(rawFilename),
          items: candidates.map((item, idx) => ({
            id: item.id || idx + 1,
            title: item.title || 'Sin título',
            videoId: item.videoId || '',
            thumbnail: item.thumbnail || (item.videoId ? `https://i.ytimg.com/vi/${item.videoId}/default.jpg` : ''),
            played: Boolean(item.played),
            assignedTo: item.assignedTo || '',
            startTime: Math.max(0, Number(item.startTime ?? item.t ?? 0) || 0),
            endTime: Math.max(0, Number(item.endTime ?? 0) || 0)
          })),
          version: parsed.version || '2.0'
        };
      }
    }

    // Formato legado v1: Array directo de items
    if (Array.isArray(parsed)) {
      return {
        name: extractNameFromFilename(rawFilename),
        items: parsed.map((item, idx) => ({
          id: item.id || idx + 1,
          title: item.title || 'Sin título',
          videoId: item.videoId || '',
          thumbnail: item.thumbnail || (item.videoId ? `https://i.ytimg.com/vi/${item.videoId}/default.jpg` : ''),
          played: Boolean(item.played),
          assignedTo: item.assignedTo || '',
          startTime: Math.max(0, Number(item.startTime ?? item.t ?? 0) || 0),
          endTime: Math.max(0, Number(item.endTime ?? 0) || 0)
        })),
        version: '1.0'
      };
    }
  }

  // Intento 2: Si no es JSON, verificar si es una lista de líneas de texto (ej: enlaces de YouTube o títulos)
  const lines = cleanText.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  if (lines.length > 0) {
    const textItems = [];
    lines.forEach((line, idx) => {
      let vidId = '';
      let parsedStartTime = 0;
      let parsedEndTime = 0;
      const ytMatch = line.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([\w-]{11})/);
      if (ytMatch && ytMatch[1]) {
        vidId = ytMatch[1];
        const tMatch = line.match(/[?&]t=([0-9msh]+)/i);
        if (tMatch && tMatch[1]) {
          parsedStartTime = parseTimeStringToSeconds(tMatch[1]);
        }
        const endMatch = line.match(/[?&]end=([0-9msh]+)/i);
        if (endMatch && endMatch[1]) {
          parsedEndTime = parseTimeStringToSeconds(endMatch[1]);
        }
      }

      textItems.push({
        id: idx + 1,
        title: vidId ? `Video ${vidId}` : line,
        videoId: vidId,
        thumbnail: vidId ? `https://i.ytimg.com/vi/${vidId}/default.jpg` : '',
        played: false,
        assignedTo: '',
        startTime: parsedStartTime,
        endTime: parsedEndTime
      });
    });

    if (textItems.length > 0) {
      return {
        name: extractNameFromFilename(rawFilename),
        items: textItems,
        version: 'plain-text'
      };
    }
  }

  throw new Error('El archivo seleccionado no tiene un formato válido de lista de canciones (JSON o texto).');
};

/**
 * Extrae un nombre de lista a partir del nombre del archivo:
 * ej: garaoke-2026-09-04_Rock_Nacional.txt -> Rock Nacional
 */
export const extractNameFromFilename = (filename) => {
  if (!filename) return 'Lista importada';
  let clean = filename.replace(/\.txt$/i, '');
  // Si coincide con el patrón garaoke-YYYY-MM-DD_Nombre
  const patternMatch = clean.match(/^garaoke-\d{4}-\d{2}-\d{2}_(.+)$/i);
  if (patternMatch && patternMatch[1]) {
    clean = patternMatch[1];
  } else {
    clean = clean.replace(/^garaoke[_-]?/i, '');
  }
  clean = clean.replace(/_/g, ' ');
  return sanitizePlaylistName(clean).trim() || 'Lista importada';
};

export const ACTIVE_PLAYLIST_SNAPSHOT_KEY = 'garaoke_active_view_playlist';

/**
 * Guarda en localStorage el snapshot de la lista de reproducción que el usuario está viendo actualmente.
 * Esto permite que cualquier componente o ventana del reproductor consulte en tiempo real qué pistas
 * están activas y en qué orden visual exacto.
 */
export const saveActivePlaylistSnapshot = (playlistId, items = []) => {
  try {
    if (!Array.isArray(items)) return;
    const payload = {
      playlistId: playlistId || '',
      items: items.map(it => ({
        id: it.id,
        videoId: it.videoId,
        title: it.title || '',
        assignedTo: it.assignedTo || '',
        played: Boolean(it.played),
        votes: it.votes || [],
        startTime: Math.max(0, Number(it.startTime ?? it.t ?? 0) || 0),
        endTime: Math.max(0, Number(it.endTime ?? 0) || 0)
      })),
      timestamp: Date.now()
    };
    localStorage.setItem(ACTIVE_PLAYLIST_SNAPSHOT_KEY, JSON.stringify(payload));
  } catch (e) {
    console.warn('Error al guardar snapshot activo de playlist:', e);
  }
};

/**
 * Obtiene el snapshot de la lista activa en la vista del usuario.
 */
export const getActivePlaylistSnapshot = () => {
  try {
    const raw = localStorage.getItem(ACTIVE_PLAYLIST_SNAPSHOT_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
};

/**
 * Resuelve inteligentemente la siguiente pista teniendo en cuenta:
 * 1. Si la canción actual pertenece a la lista activa en pantalla (currIdx >= 0):
 *    - Busca la primera pista no reproducida posterior a currIdx en el orden visual actual.
 *    - Si todas las posteriores ya fueron reproducidas, toma la inmediatamente siguiente.
 *    - Si ya no hay más pistas hacia adelante, determina fin de playlist.
 * 2. Si el usuario cambió de playlist en pantalla durante la reproducción (currIdx === -1):
 *    - ¡La canción actual pertenecía a otra lista! Arranca inteligentemente la nueva lista desde el principio,
 *      seleccionando la primera pista NO reproducida de la nueva lista (o la primera si todas fueron reproducidas),
 *      sin repetir las que ya están marcadas como reproducidas en esa lista.
 *
 * @param {Array} items Lista de pistas en la vista actual
 * @param {string} currentVideoId ID de video de YouTube actualmente en reproducción
 * @param {Object|null} currentPlaylistItem Objeto con la información de reproducción actual
 * @param {string} playlistId Nombre o ID de la playlist activa
 * @returns {Object} { nextSong, isEndOfPlaylist, switchedPlaylist, reason }
 */
export const resolveNextTrack = (items = [], currentVideoId = null, currentPlaylistItem = null, playlistId = '') => {
  if (!Array.isArray(items) || items.length === 0) {
    return { nextSong: null, isEndOfPlaylist: true, switchedPlaylist: false, reason: 'empty_playlist' };
  }

  // Buscar índice de la canción actual en los items visuales actuales
  let currIdx = items.findIndex(it => (it.videoId && it.videoId === currentVideoId) || (it.id && it.id === currentVideoId));
  if (currIdx === -1 && currentPlaylistItem?.item) {
    currIdx = items.findIndex(it => (it.id && it.id === currentPlaylistItem.item.id) || (it.videoId && it.videoId === currentPlaylistItem.item.videoId));
  }

  // ── Caso A: La canción actual pertenece a la lista actual ──
  if (currIdx >= 0) {
    if (currIdx < items.length - 1) {
      const remaining = items.slice(currIdx + 1);
      // Priorizar la primera no reproducida después de la actual
      const nextUnplayed = remaining.find(it => !it.played && it.videoId);
      const nextCandidate = nextUnplayed || remaining.find(it => it.videoId);

      if (nextCandidate?.videoId) {
        const startSec = Math.max(0, Number(nextCandidate.startTime ?? nextCandidate.t ?? 0) || 0);
        const endSec = Math.max(0, Number(nextCandidate.endTime ?? 0) || 0);
        return {
          nextSong: {
            videoId: nextCandidate.videoId,
            title: nextCandidate.title || '',
            assignedTo: nextCandidate.assignedTo || '',
            playlist: playlistId || '',
            startTime: startSec,
            endTime: endSec,
            item: {
              ...nextCandidate,
              startTime: startSec,
              endTime: endSec,
            }
          },
          isEndOfPlaylist: false,
          switchedPlaylist: false,
          reason: 'next_in_playlist'
        };
      }
    }
    // Llegó al final de la lista actual
    return { nextSong: null, isEndOfPlaylist: true, switchedPlaylist: false, reason: 'end_of_playlist' };
  }

  // ── Caso B: currIdx === -1 => El usuario cambió de playlist en la vista ──
  // Arrancar la nueva lista desde el inicio, evitando pistas ya reproducidas
  const firstUnplayed = items.find(it => !it.played && it.videoId);
  const candidate = firstUnplayed || items.find(it => it.videoId);

  if (candidate?.videoId) {
    const startSec = Math.max(0, Number(candidate.startTime ?? candidate.t ?? 0) || 0);
    const endSec = Math.max(0, Number(candidate.endTime ?? 0) || 0);
    return {
      nextSong: {
        videoId: candidate.videoId,
        title: candidate.title || '',
        assignedTo: candidate.assignedTo || '',
        playlist: playlistId || '',
        startTime: startSec,
        endTime: endSec,
        item: {
          ...candidate,
          startTime: startSec,
          endTime: endSec,
        }
      },
      isEndOfPlaylist: false,
      switchedPlaylist: true,
      reason: 'switched_playlist_start'
    };
  }

  return { nextSong: null, isEndOfPlaylist: true, switchedPlaylist: true, reason: 'no_valid_video' };
};


